import useDimensions from 'react-cool-dimensions';
import { Text as P, useTheme } from '@simplywallst/ui-core';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import { MultipleVsIndustryChartWrapper, LoadingState } from './styled';
import useWindowSize from '@/hooks/useWindowSize';
import type { CompanyIndustryHistogramBucket } from '../../../../query';
import { Column, LoadingSpinner } from '@simplywallst/ui-core';
import { useIndustryContext } from '../IndustryContextProvider';
import { useIndustryCompanies } from '../../hooks/useIndustryCompanies';
import MultipleVsIndustryChart from './MultipleVsIndustryChart';
import type { PreferredMultiple } from '../../../../selector';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';

export interface IndustryCompanies {
  companies: CompanyIndustryHistogramBucket[];
  bucketSize: number;
  maxCount: number;
  median: number | null;
  count: number;
  max: number;
}

interface Props {
  height?: number;
  heightSm?: number;
  chartWidth?: number;
  multiples: {
    [key: string]: {
      name: PreferredMultiple;
      fullName: string;
      value: string;
      valueRaw: number;
    };
  };
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalUrl?: string;
  preSelectedMultiple?: PreferredMultiple;
}

const MultipleVsIndustryChartContainer = ({
  height: inputHeight = 288,
  heightSm: inputHeightSm = 208,
  multiples,
  tickerSymbol,
  uniqueSymbol,
  canonicalUrl,
  chartWidth,
  preSelectedMultiple,
}: Props) => {
  const theme = useTheme();
  const { noirGradient01 } = useChartDefinitionsContext();
  const { observe, width } = useDimensions();
  const { index, multiple } = useIndustryContext();
  const size = useWindowSize();
  const isSmall = Number(size.width) < theme.smMaxRaw;
  const height = isSmall ? inputHeightSm : inputHeight;
  const topMargin = isSmall ? 72 : 36; //Extra space for condensed labels

  const t = useValuationTranslation();

  const {
    industryCompanies,
    preferredMultiple,
    multipleValue,
    companyHistogramStatus,
  } = useIndustryCompanies({
    tickerSymbol,
    index,
    limit: 2,
    selectedMultiple: preSelectedMultiple || multiple,
    uniqueSymbol,
    canonicalUrl,
    multiples,
  });
  const isLoadingOrError = companyHistogramStatus !== 'success';
  return (
    <MultipleVsIndustryChartWrapper ref={observe} height={height + topMargin}>
      <MultipleVsIndustryChart
        size={size}
        inputHeight={inputHeight}
        inputHeightSm={inputHeightSm}
        industryCompanies={industryCompanies}
        preferredMultiple={preferredMultiple}
        isLoading={isLoadingOrError}
        multipleValue={multipleValue}
        tickerSymbol={tickerSymbol}
        chartWidth={chartWidth || width}
        noirGradient01={noirGradient01}
      />
      {isLoadingOrError && (
        <LoadingState>
          <Column
            col={12}
            displayFlex
            justifyContent="center"
            alignItem="center"
            column
          >
            {companyHistogramStatus === 'error' && (
              <P typography="tiny">{t('vsIndustry.chart.error')}</P>
            )}
            {companyHistogramStatus === 'pending' && (
              <>
                <LoadingSpinner light />
                <br />
                <P typography="tiny">{t('vsIndustry.chart.loading')}</P>
              </>
            )}
          </Column>
        </LoadingState>
      )}
    </MultipleVsIndustryChartWrapper>
  );
};
export default MultipleVsIndustryChartContainer;
